import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  BarChart3,
  Clock3,
  Gem,
} from 'lucide-react';
import {
  formatBillingDate,
  getBillingLogLabel,
  getUsageCategory,
  USAGE_CATEGORY_META,
} from './billingData';
import { getCreditUsage } from './usageDashboardApi';

const PERIODS = [
  { value: 7, label: '近 7 天' },
  { value: 30, label: '近 30 天' },
  { value: 90, label: '近 90 天' },
  { value: 'all', label: '不限日期' },
];

export default function UsageDashboardPanel({
  user,
  preview = false,
  english = false,
}) {
  const [period, setPeriod] = useState(30);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState(null);
  const userId = user?.id;
  const disabled = preview || !userId;
  const current = result?.period === period && result?.userId === userId
    && result?.reloadKey === reloadKey;
  const loading = !disabled && (!current || result.loading);
  const error = !disabled && current && result.error;
  const data = !disabled && current && !loading && !error ? result.data : null;

  useEffect(() => {
    if (disabled) return;
    let active = true;
    const controller = new AbortController();
    const request = { period, userId, reloadKey };
    setResult({ ...request, loading: true });
    getCreditUsage(period, controller.signal)
      .then(value => {
        if (active) setResult({ ...request, data: value, loading: false });
      })
      .catch(() => {
        if (active) setResult({ ...request, error: true, loading: false });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [disabled, userId, period, reloadKey]);

  const usage = useMemo(() => {
    const groups = data?.by_type || [];
    const categories = USAGE_CATEGORY_META.map(category => {
      const matches = groups.filter(item => category.types.includes(item.type));
      return {
        ...category,
        amount: matches.reduce((sum, item) => sum + Number(item.amount), 0),
        count: matches.reduce((sum, item) => sum + Number(item.count), 0),
      };
    });
    const other = groups.filter(item => !getUsageCategory(item.type));
    if (other.length > 0) {
      categories.push({
        id: 'other',
        label: english ? 'Other usage' : '其他消耗',
        tone: 'slate',
        amount: other.reduce((sum, item) => sum + Number(item.amount), 0),
        count: other.reduce((sum, item) => sum + Number(item.count), 0),
      });
    }
    const topCategory = [...categories].sort((a, b) => b.amount - a.amount)[0];
    return {
      spendLogs: data?.recent || [],
      total: Number(data?.summary.total_credits || 0),
      count: Number(data?.summary.event_count || 0),
      categories,
      topCategory,
    };
  }, [data, english]);

  const categoryLabel = category => english ? ({
    writing: 'Writing Agent', image: 'Image generation', video: 'Video generation',
    audio: 'Audio generation', review: 'Script review', canvas: 'Canvas', other: 'Other usage',
  }[category.id] || category.label) : category.label;

  const hasUsage = usage.total > 0;
  const locale = english ? 'en-US' : 'zh-CN';
  const periodLabel = english
    ? ({ 7: 'Last 7 days', 30: 'Last 30 days', 90: 'Last 90 days', all: 'Any date' }[period])
    : PERIODS.find(item => item.value === period).label;
  const rangeLabel = data && (data.period.from
    ? `${formatBillingDate(data.period.from, { locale })} – ${formatBillingDate(data.period.to, { locale })}`
    : `${english ? 'All history through' : '全部历史，截至'} ${formatBillingDate(data.period.to, { locale })}`);

  return (
    <div className="account-content__stack">
      <section className="account-page-heading">
        <div>
          <span className="account-eyebrow">USAGE</span>
          <h1>{english ? 'Usage Dashboard' : '用量看板'}</h1>
          <p>{english ? 'View all personal credit spending within the selected period.' : '按所选时间范围统计个人点数账户的全部消耗。'}</p>
        </div>
      </section>

      <div className="billing-usage-toolbar">
        <div className="billing-segmented" role="radiogroup" aria-label={english ? 'Usage period' : '用量统计周期'}>
          {PERIODS.map(item => (
            <button
              type="button"
              role="radio"
              aria-checked={period === item.value}
              className={period === item.value ? 'is-active' : ''}
              onClick={() => setPeriod(item.value)}
              key={item.value}
            >
              {english ? ({ 7: 'Last 7 days', 30: 'Last 30 days', 90: 'Last 90 days', all: 'Any date' }[item.value]) : item.label}
            </button>
          ))}
        </div>
        <p className="billing-usage-range" role="status">
          {disabled ? (english ? 'Sign in to view your usage' : '登录后查看实际用量')
            : loading ? (english ? `Loading usage · ${periodLabel}…` : `正在加载用量 · ${periodLabel}…`)
              : error ? (english ? `Unable to load · ${periodLabel}` : `加载失败 · ${periodLabel}`)
                : `${english ? 'Period: ' : '统计范围：'}${rangeLabel}`}
        </p>
      </div>

      <div className="billing-usage-summary">
        <article>
          <span><Gem size={17} /></span>
          <small>{english ? 'Credits used in period' : '所选时段消耗'}</small>
          <strong>{loading || error ? '—' : usage.total.toLocaleString(english ? 'en-US' : 'zh-CN')}</strong>
          <em>{english ? 'credits' : '点'}</em>
        </article>
        <article>
          <span><Activity size={17} /></span>
          <small>{english ? 'Usage events' : '消耗记录'}</small>
          <strong>{loading || error ? '—' : usage.count.toLocaleString(locale)}</strong>
          <em>{english ? 'events' : '次'}</em>
        </article>
        <article>
          <span><BarChart3 size={17} /></span>
          <small>{english ? 'Top usage' : '主要消耗'}</small>
          <strong className="is-text">
            {loading || error ? '—' : hasUsage ? categoryLabel(usage.topCategory) : (english ? 'No usage' : '暂无消耗')}
          </strong>
        </article>
        <article>
          <span><Clock3 size={17} /></span>
          <small>{english ? 'Available credits' : '当前可用点数'}</small>
          <strong>{Number(data?.available_credits ?? user?.credits ?? 0).toLocaleString(locale)}</strong>
          <em>{english ? 'credits' : '点'}</em>
        </article>
      </div>

      <section className="billing-usage-breakdown">
        <header>
          <div>
            <span className="account-eyebrow">CAPABILITY BREAKDOWN</span>
            <h2>{english ? 'Capability Breakdown' : '能力消耗分布'}</h2>
          </div>
          <span>{english ? 'Measured by credits used' : '按点数消耗统计'}</span>
        </header>
        {loading ? (
          <div className="billing-usage-empty">{english ? 'Calculating usage…' : '正在计算用量…'}</div>
        ) : error ? (
          <div className="billing-usage-empty is-error" role="alert">
            <span>{english ? 'Usage could not be loaded. Please try again.' : '用量暂时无法加载，请稍后重试。'}</span>
            <button type="button" className="account-button" onClick={() => setReloadKey(value => value + 1)}>
              {english ? 'Retry' : '重新加载'}
            </button>
          </div>
        ) : (
          <div className="billing-usage-bars">
            {usage.categories.map(category => {
              const percent = usage.total > 0 ? Math.round((category.amount / usage.total) * 100) : 0;
              return (
                <article key={category.id}>
                  <div>
                    <strong>{categoryLabel(category)}</strong>
                    <span>{category.count} {english ? 'events' : '次'} · {category.amount.toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</span>
                  </div>
                  <div className="billing-usage-bars__track">
                    <i
                      className={`is-${category.tone}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <b>{percent}%</b>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="billing-usage-recent">
        <header>
          <div>
            <span className="account-eyebrow">RECENT USAGE</span>
            <h2>{english ? 'Recent Usage' : '最近消耗'}</h2>
          </div>
          <span>{english ? 'Latest 8 entries in this period; totals include all entries' : '展示所选时段最近 8 条，汇总包含全部记录'}</span>
        </header>
        {!loading && !error && usage.spendLogs.length > 0 ? (
          <div>
            {usage.spendLogs.slice(0, 8).map(item => (
              <article key={item.id}>
                <div>
                  <strong>{getBillingLogLabel(item, english ? 'en-US' : 'zh-CN')}</strong>
                  {item.note && <span>{item.note}</span>}
                </div>
                <time dateTime={item.created_at || undefined}>
                  {formatBillingDate(item.created_at, { locale: english ? 'en-US' : 'zh-CN' })}
                </time>
                <b>{Math.abs(Number(item.amount || 0)).toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</b>
              </article>
            ))}
          </div>
        ) : (
          <div className="billing-usage-empty">
            {english ? (loading ? 'Loading…' : error ? 'Usage could not be loaded.' : 'No spending records in this period') : (loading ? '正在加载…' : error ? '用量暂时无法加载。' : '所选时段暂无消耗记录')}
          </div>
        )}
      </section>

    </div>
  );
}
